import { Fragment, useEffect, useRef, type ReactNode, type RefObject } from 'react';

import { formatHours, type ApprovalInboxEntry } from '@time-tracking/shared';

import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { hoursBucket, hoursBucketClass, HOURS_TOLERANCE_MINUTES } from '../../utils/hours.js';
import { ActivityIcon } from '../icons/ActivityIcon.js';
import { CheckIcon } from '../icons/CheckIcon.js';
import { NoteIcon } from '../icons/NoteIcon.js';
import { UserPlusIcon } from '../icons/UserPlusIcon.js';
import { StatusPill } from '../StatusPill.js';

/** True when the keystroke belongs to a text field rather than the drawer. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true
  );
}

interface DetailDrawerProps {
  entry: ApprovalInboxEntry | null;
  onClose: () => void;
  onApprove: (entry: ApprovalInboxEntry) => void;
  onReturn: (entry: ApprovalInboxEntry) => void;
  onUnapprove: (entry: ApprovalInboxEntry) => void;
  isApprovePending: boolean;
  isUnapprovePending: boolean;
  canTakeAction?: boolean;
  /**
   * A closed reporting period owns this week. The approval actions are withheld and
   * the footer says why, rather than offering buttons the server will refuse.
   */
  periodClosed?: boolean;
  isAobo?: boolean;
  /** Opens the History view scoped to this person. Omitted → no history button. */
  onViewHistory?: (entry: ApprovalInboxEntry) => void;
  /**
   * Moves the drawer to the previous (-1) or next (+1) entry in the order the
   * approver sees on screen. Omitted → arrow/J-K stepping is off and unadvertised.
   */
  onStep?: (delta: -1 | 1) => void;
  /** Opens the team picker anchored to the callout's button. */
  onAssign?: (entry: ApprovalInboxEntry, anchorRect: DOMRect) => void;
  /**
   * True while the team picker is open over the drawer. The drawer releases its
   * own focus trap so the two don't fight over Tab — both bind document-level
   * keydown listeners, and two active traps make Tab behaviour undefined.
   */
  isPickerOpen?: boolean;
  /**
   * True while ANY modal sits above this drawer — the team picker, a Return or
   * Unapprove dialog, an assignment or bulk confirmation.
   *
   * The drawer's own trap and Escape handler must stand down for all of them, not just
   * the picker. Two live traps fight: the drawer sees the dialog's focus as outside
   * itself and pulls Tab back into the drawer behind, and a single Escape closes both
   * layers at once. Kept separate from `isPickerOpen` because only the picker needs
   * focus returned to the Assign button that opened it.
   */
  isModalAbove?: boolean;
  /**
   * Where focus lands on close when the row that opened this drawer is gone — which is
   * the ordinary case, since approving or returning from here is what removes it.
   */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Week context, shown after the EID. Set when the drawer was opened from the
   * cross-week triage list, where one person appears once per outstanding week
   * and the name alone does not say which sheet is being acted on.
   */
  periodLabel?: string | null;
  /**
   * Header identity while the detail request is still in flight. Splitting the
   * drawer onto its own endpoint turned opening it into a network round trip, and
   * `entry: null` already means "closed" — without this the panel would appear
   * only once the data landed, which reads as a dropped click.
   */
  pending?: { name: string; periodLabel: string } | null;
  /** The detail request failed. Renders a retry inside the open panel. */
  hasError?: boolean;
  onRetry?: () => void;
}

export function DetailDrawer({
  entry,
  onClose,
  onApprove,
  onReturn,
  onUnapprove,
  isApprovePending,
  isUnapprovePending,
  canTakeAction: canTakeActionProp = true,
  periodClosed = false,
  isAobo = false,
  onViewHistory,
  onStep,
  onAssign,
  isPickerOpen = false,
  isModalAbove = false,
  restoreFocusRef,
  periodLabel = null,
  pending = null,
  hasError = false,
  onRetry,
}: DetailDrawerProps) {
  // A closed period withholds the same buttons a missing approver role does.
  const canTakeAction = canTakeActionProp && !periodClosed;
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const assignButtonRef = useRef<HTMLButtonElement>(null);
  // Suspended while anything is layered above — see the isModalAbove prop note.
  const isPanelOpen = Boolean(entry) || pending !== null;
  const suspended = isPickerOpen || isModalAbove;
  // The close button is handed to the trap rather than focused by a second effect of
  // our own: two independent moves on one open transition make focus visibly jump
  // (WCAG 2.4.3), which is why useFocusTrap grew this parameter. It fires on `active`,
  // which does not change when J/K swaps the entry — so stepping still never yanks
  // focus back, as it must not.
  useFocusTrap(drawerRef, isPanelOpen && !suspended, closeButtonRef, restoreFocusRef);

  // Returning from the team picker: put focus back on the control that opened it.
  // Without this, the trap's own restore lands focus on the inbox row behind the
  // drawer, leaving a keyboard user adrift.
  const wasPickerOpen = useRef(false);
  useEffect(() => {
    if (wasPickerOpen.current && !isPickerOpen) {
      assignButtonRef.current?.focus();
    }
    wasPickerOpen.current = isPickerOpen;
  }, [isPickerOpen]);

  useEffect(() => {
    // Stand down entirely while anything is layered above. Whatever sits there owns
    // the keyboard: it has its own Escape handler, and keydown bubbles document →
    // window, so leaving this bound would close the drawer *and* the dialog on a
    // single Escape — leaving focus on <body> with nothing to restore to. It would
    // also let the stepping keys move the drawer behind the open layer.
    //
    // This used to name only the picker, which meant the Return, Unapprove and
    // assignment dialogs — all opened FROM this drawer — each ran with two Escape
    // handlers and two focus traps live at once.
    // Bound while the skeleton is showing too, not just once the entry lands: a
    // drawer you cannot dismiss until the network answers is a keyboard trap.
    if (!isPanelOpen || suspended) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (!onStep) return;
      // Known limitation: in NVDA/JAWS browse mode the arrows read the virtual
      // buffer and unmodified letters drive quick-nav, and an aria-modal dialog does
      // not by itself switch those readers into focus mode — so neither the arrows
      // nor j/k reliably arrive here. That is acceptable because closing the drawer
      // and opening the next row (Tab + Enter) remains a complete keyboard
      // equivalent — this is a shortcut, not the only path. Applies to the arrows
      // now that they are the advertised binding. VoiceOver's cursor model differs
      // again (plain arrows reach the page with QuickNav off), so all three readers
      // need the manual pass, not just the two named here (REQUIREMENTS N-11).
      //
      // Never hijack a keystroke meant for a text field, and leave shortcut
      // combinations (Cmd+J and friends) to the browser.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (isTypingTarget(e.target)) return;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        onStep(1);
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        onStep(-1);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, onClose, onStep, suspended]);

  if (!entry) {
    if (!pending) return null;
    // Same shell, skeleton body: the header is already known from the row that
    // was clicked, so only the breakdown waits.
    return (
      <>
        <div className="drawer-backdrop" onClick={onClose} />
        {/* Deliberately the same element type, className and tree position as the
            loaded drawer below. React reconciles host elements positionally: keep them
            identical and it updates this node in place when `entry` arrives, so the
            focus trap's captured container stays live and focus survives the swap.
            Diverge — even `aside` vs `div` — and it remounts, focus falls to <body>,
            and Tab dies against a detached container. */}
        <div
          ref={drawerRef}
          className="detail-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby="drawer-title"
        >
          <div className="drawer-header">
            <div className="drawer-header__identity">
              <div>
                <h2 id="drawer-title" className="drawer-title">
                  {pending.name}
                </h2>
                <p className="drawer-subtitle">
                  <b className="drawer-subtitle__period">{pending.periodLabel}</b>
                </p>
              </div>
            </div>
            <button
              ref={closeButtonRef}
              type="button"
              className="drawer-close"
              onClick={onClose}
              aria-label="Close detail panel"
            >
              &times;
            </button>
          </div>
          <div className="drawer-body">
            {hasError ? (
              <div className="error-banner" role="alert">
                <p>Couldn&rsquo;t load this timesheet.</p>
                {onRetry && (
                  <button type="button" className="btn btn--sm btn--light" onClick={onRetry}>
                    Try again
                  </button>
                )}
              </div>
            ) : (
              <div className="drawer-skeleton" aria-busy="true" aria-live="polite">
                <span className="sr-only">Loading this timesheet…</span>
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="drawer-skeleton__row" />
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // "Mon May 4, 9:14 AM". Intl's en-US default puts a comma after the weekday
  // ("Mon, May 4, …"); the parts are assembled by hand to drop it and match the
  // prototype's string.
  const submittedAt = entry.submittedAt
    ? (() => {
        const d = new Date(entry.submittedAt);
        const day = new Intl.DateTimeFormat('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
          .format(d)
          .replace(',', '');
        const time = new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: '2-digit',
        }).format(d);
        return `${day}, ${time}`;
      })()
    : null;

  const showLines = entry.status === 'submitted' || entry.status === 'approved';
  const displayMinutes = showLines ? entry.totalMinutes : 0;
  const hasVisibleLines = showLines && entry.breakdown.length > 0;

  // Copy for the empty "Hours by line" state. A returned sheet *was* submitted and
  // sent back, so "not submitted" would misdescribe it — the prototype has no
  // returned-drawer state, so this case is filled in here.
  const emptyLinesCopy =
    entry.status === 'returned'
      ? {
          title: 'Sheet returned for fix',
          body: `${entry.name} hasn't resubmitted hours for this week yet.`,
        }
      : {
          title: 'Sheet not submitted',
          body: `${entry.name} hasn't submitted hours for this week yet.`,
        };

  // Header subtitle: "ak4242 · Cloud · submitted Mon May 4, 9:14 AM".
  // An unassigned person shows "no team" in the team slot; a sheet that was never
  // submitted reads "no submission" rather than naming a time.
  const subtitleSegments: ReactNode[] = [];
  if (entry.utEid) subtitleSegments.push(entry.utEid);
  // Which week this drawer is acting on. Only set from the triage list, where a
  // person can appear several times and Approve would otherwise be ambiguous.
  if (periodLabel) {
    subtitleSegments.push(
      <b key="period" className="drawer-subtitle__period">
        {periodLabel}
      </b>,
    );
  }
  if (entry.teamId === null) {
    subtitleSegments.push(
      <span key="no-team" className="drawer-subtitle__no-team">
        no team
      </span>,
    );
  } else if (entry.teamName) {
    subtitleSegments.push(entry.teamName);
  }
  if (submittedAt) {
    subtitleSegments.push(`submitted ${submittedAt}`);
  } else if (entry.status === 'missing') {
    subtitleSegments.push('no submission');
  }

  // Always numeric once there are hours to compare — "+0h" rather than "On target",
  // so the three glance cards read as a consistent row of figures.
  const vsTarget = (() => {
    if (!showLines) return '—';
    const diff = displayMinutes - entry.targetMinutes;
    return diff >= 0 ? `+${formatHours(diff)}h` : `−${formatHours(Math.abs(diff))}h`;
  })();

  // Highest-minutes project, aggregated *per project code* — a project split across
  // several lines should out-rank a single larger line under a different code.
  const topProject = (() => {
    if (!showLines) return null;
    const byCode = new Map<string, number>();
    for (const b of entry.breakdown) {
      if (!b.projectCode) continue;
      byCode.set(b.projectCode, (byCode.get(b.projectCode) ?? 0) + b.totalMinutes);
    }
    const top = [...byCode.entries()].sort((a, b) => b[1] - a[1])[0];
    if (!top) return null;
    const [code, minutes] = top;
    const pct = displayMinutes > 0 ? Math.round((minutes / displayMinutes) * 100) : 0;
    return { code, note: `${formatHours(minutes)}h · ${pct}% of week` };
  })();

  // "consistent" when the week is within tolerance of the running average, so the
  // approver only reads a delta when there is one worth reading.
  //
  // The three cases are distinct: no history at all, history but nothing to compare
  // it against this week, and a real comparison. Collapsing the first two would put
  // "no history" under a visible average figure, which contradicts itself.
  const avgNote = (() => {
    if (entry.avgPrev4Minutes === null) return 'no history';
    if (!showLines) return 'nothing to compare';
    const diff = displayMinutes - entry.avgPrev4Minutes;
    if (Math.abs(diff) <= HOURS_TOLERANCE_MINUTES) return 'consistent';
    return diff > 0 ? `+${formatHours(diff)}h vs avg` : `−${formatHours(Math.abs(diff))}h vs avg`;
  })();

  // Only advertise stepping when it is actually wired up by the page. The arrows are
  // advertised rather than J/K because none of the approvers this shipped to are vim
  // users and the J/K hint confused all of them. J/K stays wired, just undocumented.
  //
  // "up/down arrows" not "arrow keys": only Up/Down are bound, and left/right doing
  // nothing after the hint implies otherwise reads as broken. Lowercase because the
  // house style capitalises named keycaps (Esc, Ctrl) but not descriptive phrases —
  // see NotePopover's "⌘↵ / Ctrl↵ to save · Esc to cancel".
  const keyboardHint = onStep ? 'Esc to close · up/down arrows for prev/next' : 'Esc to close';

  const isUnassigned = entry.teamId === null;

  // First name for the read-only "<First>'s note" callout label (uppercased in CSS).
  // Trim + split on any whitespace so leading/multiple spaces don't yield "'s note".
  const firstName = entry.name.trim().split(/\s+/)[0] || entry.name.trim();

  // Sheet-level submit comment: shown once the sheet has been submitted (also on
  // approved/returned), never on a draft or missing sheet.
  const showSubmitComment =
    entry.submitComment !== null &&
    entry.submitComment.trim() !== '' &&
    (entry.status === 'submitted' || entry.status === 'approved' || entry.status === 'returned');

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div
        ref={drawerRef}
        className="detail-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        <div className="drawer-header">
          <div className="drawer-header__identity">
            <div className="drawer-avatar" aria-hidden="true">
              {entry.initials}
            </div>
            <div>
              <h2 id="drawer-title" className="drawer-title">
                {entry.name}
              </h2>
              <p className="drawer-subtitle">
                {/* EID leads, then team (or "no team"), then submission state.
                    Each segment is dropped rather than rendered empty, so a user
                    with no EID doesn't get a leading separator. */}
                {subtitleSegments.map((segment, i) => (
                  <Fragment key={i}>
                    {i > 0 && ' · '}
                    {segment}
                  </Fragment>
                ))}
              </p>
            </div>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close detail panel"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path
                d="M2 2l12 12M14 2L2 14"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                fill="none"
              />
            </svg>
          </button>
        </div>

        <div className="drawer-body">
          <div className="drawer-week-total">
            <div className="drawer-week-total__top">
              <div>
                <p className="drawer-week-total__label">Week total</p>
                <p
                  className={`drawer-week-total__hours ${hoursBucketClass(
                    hoursBucket(displayMinutes, entry.targetMinutes),
                  )}`}
                >
                  {formatHours(displayMinutes)}
                  <span className="drawer-week-total__unit">h</span>
                </p>
                <p className="drawer-week-total__meta">
                  target {formatHours(entry.targetMinutes)}h
                  {entry.avgPrev4Minutes !== null &&
                    ` · 4-week avg ${formatHours(entry.avgPrev4Minutes)}h`}
                </p>
              </div>
              <StatusPill status={entry.status} />
            </div>
          </div>

          {/* The submission note sits above the anomaly card: it usually frames the
              flags below it ("I ran over to hit the go-live"), so reading it first
              gives the approver the contributor's own account before the warnings. */}
          {showSubmitComment && (
            <div className="submit-message" data-testid="submit-comment-callout">
              <span className="submit-message__avatar" aria-hidden="true">
                {entry.initials}
              </span>
              <div className="submit-message__body">
                <span className="submit-message__label">
                  {firstName} added a note when submitting
                </span>
                <blockquote className="submit-message__quote">{entry.submitComment}</blockquote>
              </div>
            </div>
          )}

          {isUnassigned && (
            <div className="unassigned-callout" data-testid="unassigned-callout">
              <span className="unassigned-callout__icon" aria-hidden="true">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  focusable="false"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* unlink — a *broken* chain. An intact link here would read as
                      the opposite of "Not on any team". */}
                  <path d="M18.84 12.25 19.9 11.2a4.5 4.5 0 0 0-6.36-6.37l-1.06 1.06" />
                  <path d="M5.17 11.75 4.1 12.8a4.5 4.5 0 0 0 6.37 6.37l1.06-1.06" />
                  <line x1="2" y1="2" x2="22" y2="22" />
                </svg>
              </span>
              <div className="unassigned-callout__body">
                <p className="unassigned-callout__title">Not on any team</p>
                <p className="unassigned-callout__text">
                  This person has no approver of record. Approving here is a one-off — add them to a
                  team to fix it for future weeks.
                </p>
              </div>
              {onAssign && (
                <button
                  ref={assignButtonRef}
                  type="button"
                  className="btn unassigned-callout__action"
                  onClick={(e) => onAssign(entry, e.currentTarget.getBoundingClientRect())}
                  aria-haspopup="dialog"
                  aria-expanded={isPickerOpen}
                >
                  <UserPlusIcon />
                  Add to team…
                </button>
              )}
            </div>
          )}

          {entry.anomalies.length > 0 && (
            <div className="drawer-anomaly-card" role="note">
              <p className="drawer-anomaly-card__header">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M8 1L1 14h14L8 1zm0 2.8L13.1 13H2.9L8 3.8zm-.75 3.7v2.5h1.5V7.5h-1.5zm0 3.5v1.5h1.5V11h-1.5z" />
                </svg>
                {entry.anomalies.length} thing{entry.anomalies.length > 1 ? 's' : ''} to look at
              </p>
              {/* Kept as a list: a set of anomalies genuinely is one, and the
                  prototype's plain divs would drop the semantics. Styled as
                  spaced block rows to match it visually. */}
              <ul className="drawer-anomaly-card__list">
                {entry.anomalies.map((a, i) => (
                  <li key={i}>{a.label}</li>
                ))}
              </ul>
            </div>
          )}

          {/* The section title renders unconditionally — an approver opening a sheet
              with nothing on it should still see where the hours would be, with an
              explicit empty state rather than a silently absent section. */}
          <section className="drawer-section" aria-labelledby="breakdown-heading">
            <h3 id="breakdown-heading" className="drawer-section-title">
              Hours by line
            </h3>
            {hasVisibleLines ? (
              // The wrapper carries the card's border + radius; the table can't clip
              // its own corners reliably with border-collapse.
              <div className="breakdown">
                <table className="breakdown-table">
                  <caption className="sr-only">Hours by line</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="breakdown-col-task">
                        Task
                      </th>
                      <th scope="col" className="breakdown-col-project">
                        Project
                      </th>
                      <th scope="col" className="breakdown-col-type">
                        Type
                      </th>
                      <th scope="col" className="breakdown-col-hours">
                        Hours
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.breakdown.map((b) => (
                      <Fragment key={b.lineId}>
                        <tr className={b.note ? 'breakdown-row has-note' : 'breakdown-row'}>
                          <td>
                            <span className="breakdown-task">
                              <span className="breakdown-task__label">{b.label}</span>
                              {b.note && (
                                <span className="note-marker" data-testid="line-note-marker">
                                  <NoteIcon />
                                </span>
                              )}
                            </span>
                          </td>
                          <td className="breakdown-project">{b.projectCode ?? '—'}</td>
                          <td className="breakdown-type">{b.isWorkTime ? 'Standard' : 'Leave'}</td>
                          <td className="breakdown-hours">{formatHours(b.totalMinutes)}h</td>
                        </tr>
                        {b.note && (
                          <tr className="breakdown-note-row">
                            <td
                              colSpan={4}
                              className="breakdown-note"
                              data-testid="line-note-callout"
                            >
                              <div className="breakdown-note__inner">
                                <NoteIcon className="note-icon" />
                                <div className="note-body">
                                  <span className="note-from">{firstName}&apos;s note</span>
                                  <p>{b.note}</p>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="breakdown-total-row">
                      <td colSpan={3} className="breakdown-total-row__label">
                        Week total
                      </td>
                      <td className="breakdown-hours">{formatHours(displayMinutes)}h</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="breakdown-empty" data-testid="breakdown-empty">
                <span className="breakdown-empty__icon" aria-hidden="true">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    focusable="false"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8v4" />
                    <path d="M12 16h.01" />
                  </svg>
                </span>
                <h4 className="breakdown-empty__title">{emptyLinesCopy.title}</h4>
                <p className="breakdown-empty__body">{emptyLinesCopy.body}</p>
              </div>
            )}
          </section>

          <section
            className="drawer-section drawer-section--glance"
            aria-labelledby="glance-heading"
          >
            <h3 id="glance-heading" className="drawer-section-title">
              At a glance
            </h3>
            <div className="glance-cards">
              <div className="glance-card">
                <span className="glance-card__label">4-week avg</span>
                <span className="glance-card__value">
                  {entry.avgPrev4Minutes !== null ? `${formatHours(entry.avgPrev4Minutes)}h` : '—'}
                </span>
                {/* Always a note line, so the three cards stay the same height. */}
                <span className="glance-card__note">{avgNote}</span>
              </div>
              <div className="glance-card">
                <span className="glance-card__label">Top project</span>
                <span className="glance-card__value">{topProject?.code ?? '—'}</span>
                <span className="glance-card__note">{topProject?.note ?? 'no entries'}</span>
              </div>
              <div className="glance-card">
                <span className="glance-card__label">vs target</span>
                <span className="glance-card__value">{vsTarget}</span>
                <span className="glance-card__note">
                  target {formatHours(entry.targetMinutes)}h/wk
                </span>
              </div>
            </div>
          </section>
        </div>

        <div className="drawer-footer">
          <div className="drawer-footer__lead">
            {onViewHistory && (
              <button
                type="button"
                className="btn btn--ghost drawer-history-btn"
                onClick={() => onViewHistory(entry)}
                title={`See ${firstName}'s past weekly submissions`}
              >
                <ActivityIcon />
                View full history
              </button>
            )}
            {/* Always shown. The prototype hides this for AOBO to save a line, but
                stepping keeps working in that case, and an undiscoverable shortcut is
                worse than a slightly busier footer — which now wraps anyway. */}
            <p className="drawer-footer__hint">{keyboardHint}</p>
          </div>
          <div className="drawer-footer__actions">
            {entry.status === 'returned' && (
              <span className="drawer-waiting">Waiting on {firstName} to resubmit.</span>
            )}
            {/* R30: a missing sheet's footer is hint text only. Without it the footer is
                blank apart from the keyboard hint, so a manager who opens a not-submitted
                week sees no action button and nothing explaining why. */}
            {entry.status === 'missing' && (
              <span className="drawer-waiting">
                Nothing to review yet — {firstName} hasn&rsquo;t submitted this week.
              </span>
            )}
            {/* No one-off counterpart here (prototype item 22's footer note): the
                "Approve as one-off" button sits directly beneath it, the body carries
                the full callout, and the header already tags "no team". */}
            {periodClosed && (entry.status === 'submitted' || entry.status === 'approved') && (
              <span className="drawer-waiting">
                This reporting period is closed, so this sheet can&rsquo;t be approved, returned or
                unapproved. An administrator can reopen the period.
              </span>
            )}
            {!periodClosed &&
              isAobo &&
              entry.status === 'submitted' &&
              entry.teamLeadDisplayName && (
                <span className="drawer-aobo-note">
                  On behalf of <strong>{entry.teamLeadDisplayName}</strong>
                </span>
              )}
            <div className="drawer-footer__buttons">
              {canTakeAction && entry.status === 'submitted' && entry.timesheetId && (
                <>
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => onReturn(entry)}
                    aria-label={`Return ${entry.name}'s timesheet for correction`}
                  >
                    Return for fix
                  </button>
                  <button
                    type="button"
                    className="btn primary"
                    onClick={() => onApprove(entry)}
                    disabled={isApprovePending}
                    aria-busy={isApprovePending}
                    aria-label={`Approve ${entry.name}'s timesheet`}
                  >
                    {isApprovePending ? (
                      'Approving…'
                    ) : isAobo ? (
                      <>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          <polyline points="9 12 11 14 15 10" />
                        </svg>
                        Approve on behalf of
                      </>
                    ) : (
                      <>
                        <CheckIcon />
                        {/* Naming it a one-off keeps the approver honest about what
                            they're doing: releasing this week only, not fixing the
                            missing approver-of-record. */}
                        {isUnassigned ? 'Approve as one-off' : 'Approve sheet'}
                      </>
                    )}
                  </button>
                </>
              )}
              {canTakeAction && entry.status === 'approved' && entry.timesheetId && (
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => onUnapprove(entry)}
                  disabled={isUnapprovePending}
                  aria-busy={isUnapprovePending}
                  aria-label={`Unapprove ${entry.name}'s timesheet`}
                >
                  {isUnapprovePending ? 'Unapproving…' : 'Unapprove'}
                </button>
              )}
              <button type="button" className="btn btn--secondary" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
